import { Product } from '@shared/components/ui';
import { Button, ModalCenter, ModalCenterRef } from '@shared/components/ui';
import { Alert, AlertDescription } from '@shared/components/ui/shadcn/alert';
import { Badge } from '@shared/components/ui/shadcn/badge';
import { Input } from '@shared/components/ui/shadcn/input';
import { Label } from '@shared/components/ui/shadcn/label';
import { ScrollArea } from '@shared/components/ui/shadcn/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@shared/components/ui/shadcn/tabs';
import { EntryLine, EntryReceiptMovement, SeparationTime } from '../../../models/Entry';
import { SeparationTimeProduct } from '../../adapters/ReceiptApiAdapter';
import { forwardRef, useImperativeHandle, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, Plus, Trash2, AlertCircle, User, List } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen } from '@fortawesome/pro-light-svg-icons';

const buildImageUrl = (imageUrl?: string | null): string => {
  if (!imageUrl) return '';
  if (imageUrl.startsWith('http')) return imageUrl;
  return `https://warehouse-images-stg-dev.nyc3.digitaloceanspaces.com/${imageUrl}`;
};

const formatDate = (dateString?: string | null) => {
  if (!dateString) return '-';
  const isoDateString = dateString.replace(' ', 'T');
  const date = new Date(isoDateString);
  if (isNaN(date.getTime())) return dateString;

  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');

  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const formatTimeMinutes = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = Math.floor(minutes % 60);
  const secs = Math.floor((minutes % 1) * 60);

  if (hours > 0) {
    return `${hours}h ${mins}m ${secs}s`;
  }
  if (mins > 0) {
    return `${mins}m ${secs}s`;
  }
  return `${secs}s`;
};

export interface SeparationTimeModalRef {
  open: (
    entryLines: EntryLine[],
    movements: EntryReceiptMovement[],
    separationTimes: SeparationTime[] | undefined,
    onConfirm: (products: SeparationTimeProduct[], timeMinutes: number) => Promise<void>,
    onRefresh?: () => Promise<SeparationTime[] | undefined>,
    onUpdate?: (separationTimeId: number, products: SeparationTimeProduct[], timeMinutes: number) => Promise<void>,
  ) => void;
  updateSeparationTimes: (separationTimes: SeparationTime[] | undefined) => void;
}

interface ProductWithAvailableQty {
  product_id: number;
  product_name: string;
  product_code: string;
  product_image?: string | null;
  accepted_qty: number;
  already_separated_qty: number;
  available_qty: number;
}

export const SeparationTimeModal = forwardRef<SeparationTimeModalRef>((_, ref) => {
  const { t } = useTranslation('merchandiseV3');
  const modalRef = useRef<ModalCenterRef>(null);
  const [entryLines, setEntryLines] = useState<EntryLine[]>([]);
  const [movements, setMovements] = useState<EntryReceiptMovement[]>([]);
  const [separationTimes, setSeparationTimes] = useState<SeparationTime[] | undefined>(undefined);
  const [availableProducts, setAvailableProducts] = useState<ProductWithAvailableQty[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<Array<{ product_id: number; qty: number }>>([]);
  const [timeMinutes, setTimeMinutes] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('register');
  const [editingSeparationTimeId, setEditingSeparationTimeId] = useState<number | null>(null);
  const registerConfirmCallbackRef = useRef<
    ((products: SeparationTimeProduct[], timeMinutes: number) => Promise<void>) | null
  >(null);
  const updateConfirmCallbackRef = useRef<
    ((separationTimeId: number, products: SeparationTimeProduct[], timeMinutes: number) => Promise<void>) | null
  >(null);
  const refreshCallbackRef = useRef<(() => Promise<SeparationTime[] | undefined>) | null>(null);

  useImperativeHandle(ref, () => ({
    open: (
      entryLinesData: EntryLine[],
      movementsData: EntryReceiptMovement[],
      separationTimesData: SeparationTime[] | undefined,
      onConfirm: (products: SeparationTimeProduct[], timeMinutes: number) => Promise<void>,
      onRefresh?: () => Promise<SeparationTime[] | undefined>,
      onUpdate?: (separationTimeId: number, products: SeparationTimeProduct[], timeMinutes: number) => Promise<void>,
    ) => {
      setEntryLines(entryLinesData);
      setMovements(movementsData);
      setSeparationTimes(separationTimesData);
      setSelectedProducts([]);
      setTimeMinutes('');
      setEditingSeparationTimeId(null);
      setIsProcessing(false);
      setError(null);
      setActiveTab('register');
      registerConfirmCallbackRef.current = onConfirm;
      refreshCallbackRef.current = onRefresh || null;
      updateConfirmCallbackRef.current = onUpdate || null;
      modalRef.current?.openModal();
    },
    updateSeparationTimes: (separationTimesData: SeparationTime[] | undefined) => {
      setSeparationTimes(separationTimesData);
    },
  }));

  // Calcular productos disponibles con cantidades aceptadas
  useEffect(() => {
    if (entryLines.length === 0 || movements.length === 0) {
      setAvailableProducts([]);
      return;
    }

    // Calcular cantidad aceptada por producto (solo movimientos ACCEPTED)
    const acceptedByProduct = new Map<number, number>();
    movements
      .filter((m) => m.movement_type === 'ACCEPTED')
      .forEach((m) => {
        const current = acceptedByProduct.get(m.product_id) || 0;
        acceptedByProduct.set(m.product_id, current + m.qty);
      });

    // Calcular cantidad ya separada desde separation_times
    const separatedByProduct = new Map<number, number>();
    if (separationTimes && separationTimes.length > 0) {
      separationTimes.forEach((separationTime) => {
        // Si estamos editando, excluimos el registro actual de los ya separados
        // para que la validación de "no exceder aceptado" sea correcta.
        if (editingSeparationTimeId && separationTime.id === editingSeparationTimeId) {
          return;
        }
        separationTime.items.forEach((item) => {
          const current = separatedByProduct.get(item.product_id) || 0;
          separatedByProduct.set(item.product_id, current + item.qty);
        });
      });
    }

    const products: ProductWithAvailableQty[] = entryLines
      .filter((line) => {
        const acceptedQty = acceptedByProduct.get(line.product_id) || 0;
        return acceptedQty > 0;
      })
      .map((line) => {
        const acceptedQty = acceptedByProduct.get(line.product_id) || 0;
        const alreadySeparatedQty = separatedByProduct.get(line.product_id) || 0;
        const availableQty = acceptedQty - alreadySeparatedQty;

        return {
          product_id: line.product_id,
          product_name: line.product?.name || `Producto ${line.product_id}`,
          product_code: line.product?.code || '',
          product_image: line.product?.image_hash || line.product?.img || null,
          accepted_qty: acceptedQty,
          already_separated_qty: alreadySeparatedQty,
          available_qty: Math.max(0, availableQty),
        };
      })
      .filter((p) => p.available_qty > 0)
      .sort((a, b) => a.product_name.localeCompare(b.product_name));

    setAvailableProducts(products);
  }, [entryLines, movements, separationTimes, editingSeparationTimeId]);

  const handleAddProduct = (productId: number) => {
    const product = availableProducts.find((p) => p.product_id === productId);
    if (!product) return;

    const existingIndex = selectedProducts.findIndex((p) => p.product_id === productId);
    if (existingIndex >= 0) {
      // Ya está seleccionado, incrementar cantidad
      const current = selectedProducts[existingIndex];
      if (current.qty < product.available_qty) {
        const updated = [...selectedProducts];
        updated[existingIndex] = { ...current, qty: current.qty + 1 };
        setSelectedProducts(updated);
      }
    } else {
      // Nuevo producto
      setSelectedProducts([...selectedProducts, { product_id: productId, qty: 1 }]);
    }
    setError(null);
  };

  const handleRemoveProduct = (productId: number) => {
    setSelectedProducts(selectedProducts.filter((p) => p.product_id !== productId));
    setError(null);
  };

  const handleQtyChange = (productId: number, newQty: number) => {
    const product = availableProducts.find((p) => p.product_id === productId);
    if (!product) return;

    const qty = Math.max(1, Math.min(newQty, product.available_qty));
    const updated = selectedProducts.map((p) => (p.product_id === productId ? { ...p, qty } : p));
    setSelectedProducts(updated);
    setError(null);
  };

  const handleQtyInputChange = (productId: number, rawValue: string) => {
    // Permite limpiar y reescribir fácilmente sin depender de las flechas del input number.
    if (rawValue === '') return;
    handleQtyChange(productId, Number(rawValue));
  };

  const handleQtyInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // En navegación con Tab, saltar directo al campo de tiempo total.
    if (event.key !== 'Tab' || event.shiftKey) return;

    const timeInput = document.querySelector<HTMLInputElement>('[data-testid="separation-time-time-input"]');
    if (!timeInput) return;

    event.preventDefault();
    timeInput.focus();
    timeInput.select();
  };

  const handleConfirm = async () => {
    const separationTimeId = editingSeparationTimeId;
    const isEditMode = separationTimeId !== null;
    setError(null);

    // Validaciones
    if (selectedProducts.length === 0) {
      setError(t('separation_time.error.no_products', 'Debe seleccionar al menos un producto'));
      return;
    }

    const timeValue = parseFloat(timeMinutes);
    if (isNaN(timeValue) || timeValue <= 0) {
      setError(t('separation_time.error.invalid_time', 'El tiempo debe ser mayor a 0 minutos'));
      return;
    }

    if (timeValue < 0.01) {
      setError(t('separation_time.error.min_time', 'El tiempo mínimo es 0.01 minutos'));
      return;
    }

    // Validar cantidades
    for (const selected of selectedProducts) {
      const product = availableProducts.find((p) => p.product_id === selected.product_id);
      if (!product) {
        setError(t('separation_time.error.product_not_found', 'Producto no encontrado'));
        return;
      }

      if (selected.qty > product.available_qty) {
        setError(
          t(
            'separation_time.error.qty_exceeds',
            {
              product: product.product_name,
              available: product.available_qty,
            },
            `La cantidad para ${product.product_name} excede lo disponible (${product.available_qty} unidades)`,
          ),
        );
        return;
      }
    }

    setIsProcessing(true);
    try {
      const products: SeparationTimeProduct[] = selectedProducts.map((p) => ({
        product_id: p.product_id,
        qty: p.qty,
      }));

      if (isEditMode) {
        if (!updateConfirmCallbackRef.current) {
          throw new Error('onUpdate no definido');
        }
        await updateConfirmCallbackRef.current(separationTimeId, products, timeValue);
      } else {
        if (!registerConfirmCallbackRef.current) {
          throw new Error('onConfirm no definido');
        }
        await registerConfirmCallbackRef.current(products, timeValue);
      }

      // Refrescar datos y cambiar al tab de listado
      if (refreshCallbackRef.current) {
        const updatedTimes = await refreshCallbackRef.current();
        if (updatedTimes !== undefined) {
          setSeparationTimes(updatedTimes);
        }
      }

      setSelectedProducts([]);
      setTimeMinutes('');
      setEditingSeparationTimeId(null);
      setError(null);
      setActiveTab('list');
    } catch (err: any) {
      // Manejar errores de la API
      if (err?.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err?.message) {
        setError(err.message);
      } else {
        setError(t('separation_time.error.generic', 'Error al registrar el tiempo de separación'));
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const getSelectedProductInfo = (productId: number) => {
    return availableProducts.find((p) => p.product_id === productId);
  };

  const handleStartEdit = (separationTime: SeparationTime) => {
    if (isProcessing) return;

    setEditingSeparationTimeId(separationTime.id);
    setSelectedProducts(
      separationTime.items.map((item) => ({
        product_id: item.product_id,
        qty: item.qty,
      })),
    );
    setTimeMinutes(String(separationTime.time_minutes));
    setError(null);
    setIsProcessing(false);
    setActiveTab('register');
  };

  const handleCancelRegister = () => {
    if (editingSeparationTimeId) {
      setEditingSeparationTimeId(null);
      setSelectedProducts([]);
      setTimeMinutes('');
      setError(null);
      setActiveTab('list');
      return;
    }

    modalRef.current?.closeModal();
    setSelectedProducts([]);
    setTimeMinutes('');
    setError(null);
  };

  // Ordenar tiempos por fecha (más recientes primero)
  const sortedTimes =
    separationTimes && separationTimes.length > 0
      ? [...separationTimes].sort((a, b) => {
          if (!a.created_at) return 1;
          if (!b.created_at) return -1;
          try {
            const dateA = new Date(a.created_at.replace(' ', 'T'));
            const dateB = new Date(b.created_at.replace(' ', 'T'));
            if (isNaN(dateA.getTime())) return 1;
            if (isNaN(dateB.getTime())) return -1;
            return dateB.getTime() - dateA.getTime();
          } catch {
            return 0;
          }
        })
      : [];

  return (
    <ModalCenter
      ref={modalRef}
      title={t('separation_time.title', 'Tiempos de Separación')}
      type="info"
      width="xxl"
      className="max-h-[90dvh]"
      footer={
        activeTab === 'register' ? (
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={handleCancelRegister}
              disabled={isProcessing}
              data-testid="separation-time-cancel-button"
            >
              {t('separation_time.cancel', 'Cancelar')}
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={isProcessing || selectedProducts.length === 0 || !timeMinutes}
              data-testid={
                editingSeparationTimeId ? 'separation-time-edit-save-button' : 'separation-time-register-button'
              }
            >
              {isProcessing
                ? editingSeparationTimeId
                  ? t('separation_time.edit.saving', 'Guardando...')
                  : t('separation_time.registering', 'Registrando...')
                : editingSeparationTimeId
                  ? t('separation_time.edit.save', 'Guardar cambios')
                  : t('separation_time.register', 'Registrar Tiempo')}
            </Button>
          </div>
        ) : undefined
      }
    >
      <Tabs
        value={activeTab}
        onValueChange={(value) => {
          if (value === 'list') {
            if (editingSeparationTimeId) {
              setEditingSeparationTimeId(null);
              setSelectedProducts([]);
              setTimeMinutes('');
              setError(null);
            }
          }
          setActiveTab(value);
        }}
        className="w-full"
      >
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="register" className="flex items-center gap-2">
            <Plus className="w-4 h-4" />
            {editingSeparationTimeId
              ? t('separation_time.tab.edit', 'Editar')
              : t('separation_time.tab.register', 'Registrar')}
          </TabsTrigger>
          <TabsTrigger value="list" className="flex items-center gap-2">
            <List className="w-4 h-4" />
            {t('separation_time.tab.list', 'Registrados')}
            {sortedTimes.length > 0 && (
              <Badge variant="secondary" className="ml-1">
                {sortedTimes.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Tab: Registrar */}
        <TabsContent value="register" className="space-y-6 mt-4">
          {/* Información */}
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {editingSeparationTimeId
                ? t(
                    'separation_time.edit.info',
                    'Edita los productos separados y el tiempo total invertido en minutos.',
                  )
                : t(
                    'separation_time.info',
                    'Seleccione los productos separados y el tiempo total invertido en minutos.',
                  )}
            </AlertDescription>
          </Alert>

          {/* Error */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Lista de productos disponibles */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">
              {t('separation_time.available_products', 'Productos Disponibles')} ({availableProducts.length})
            </Label>
            {availableProducts.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {t('separation_time.no_products', 'No hay productos aceptados disponibles para separar')}
              </div>
            ) : (
              <ScrollArea className="max-h-64">
                <div className="space-y-2 pr-4">
                  {availableProducts.map((product) => {
                    const isSelected = selectedProducts.some((p) => p.product_id === product.product_id);
                    const selectedQty = selectedProducts.find((p) => p.product_id === product.product_id)?.qty || 0;

                    return (
                      <div
                        key={product.product_id}
                        className={`p-3 rounded-lg border transition-colors ${
                          isSelected
                            ? 'bg-primary/5 border-primary'
                            : 'bg-background border-border hover:border-primary/50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <Product
                              id={product.product_id}
                              name={product.product_name}
                              src={buildImageUrl(product.product_image)}
                              code={product.product_code}
                              showImageModal={true}
                              enableLineClamp={true}
                            />
                          </div>
                          <div className="flex items-center gap-3 ml-4">
                            <div className="text-right text-sm">
                              <div className="text-muted-foreground">
                                {t('separation_time.accepted', 'Aceptado')}: {product.accepted_qty}
                              </div>
                              {product.already_separated_qty > 0 && (
                                <div className="text-muted-foreground">
                                  {t('separation_time.separated', 'Separado')}: {product.already_separated_qty}
                                </div>
                              )}
                              <div className="font-semibold text-foreground">
                                {t('separation_time.available', 'Disponible')}: {product.available_qty}
                              </div>
                            </div>
                            {isSelected ? (
                              <div className="flex items-center gap-2">
                                <Input
                                  type="number"
                                  min={1}
                                  max={product.available_qty}
                                  value={selectedQty}
                                  data-testid={`separation-time-qty-input-${product.product_id}`}
                                  onFocus={(e) => e.target.select()}
                                  onClick={(e) => e.currentTarget.select()}
                                  onKeyDown={handleQtyInputKeyDown}
                                  onChange={(e) => handleQtyInputChange(product.product_id, e.target.value)}
                                  className="w-20"
                                />
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRemoveProduct(product.product_id)}
                                  className="text-destructive hover:text-destructive"
                                  data-testid={`separation-time-remove-product-${product.product_id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleAddProduct(product.product_id)}
                                disabled={product.available_qty === 0}
                                data-testid={`separation-time-add-product-${product.product_id}`}
                              >
                                <Plus className="w-4 h-4 mr-1" />
                                {t('separation_time.add', 'Agregar')}
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            )}
          </div>

          {/* Productos seleccionados */}
          {selectedProducts.length > 0 && (
            <div className="space-y-3">
              <Label className="text-sm font-medium">
                {t('separation_time.selected_products', 'Productos Seleccionados')} ({selectedProducts.length})
              </Label>
              <div className="space-y-2">
                {selectedProducts.map((selected) => {
                  const product = getSelectedProductInfo(selected.product_id);
                  if (!product) return null;

                  return (
                    <div
                      key={selected.product_id}
                      className="flex items-center justify-between p-3 bg-primary/5 border border-primary rounded-lg"
                    >
                      <div className="flex-1 min-w-0">
                        <Product
                          id={product.product_id}
                          name={product.product_name}
                          src={buildImageUrl(product.product_image)}
                          code={product.product_code}
                          showImageModal={true}
                          enableLineClamp={true}
                        />
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <span className="text-sm font-semibold text-foreground">
                          {selected.qty} {t('separation_time.units', 'unidades')}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Tiempo */}
          <div className="space-y-2">
            <Label htmlFor="time_minutes" className="text-sm font-medium">
              {t('separation_time.time_label', 'Tiempo Total (minutos)')}
            </Label>
            <div className="relative">
              <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="time_minutes"
                type="number"
                step="0.01"
                min="0.01"
                value={timeMinutes}
                data-testid="separation-time-time-input"
                onFocus={(e) => e.target.select()}
                onClick={(e) => e.currentTarget.select()}
                onChange={(e) => {
                  setTimeMinutes(e.target.value);
                  setError(null);
                }}
                placeholder={t('separation_time.time_placeholder', 'Ej: 15.5 (15 minutos y 30 segundos)')}
                className="pl-10"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {t(
                'separation_time.time_hint',
                'Ingrese el tiempo total invertido en separar todos los productos seleccionados',
              )}
            </p>
          </div>
        </TabsContent>

        {/* Tab: Listado */}
        <TabsContent value="list" className="space-y-4 mt-4">
          {sortedTimes.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
              <p className="text-muted-foreground">
                {t('separation_time.modal.empty', 'No hay tiempos de separación registrados')}
              </p>
            </div>
          ) : (
            <ScrollArea className="h-[500px]">
              <div className="space-y-3 pr-4">
                {sortedTimes.map((separationTime) => {
                  const userName =
                    separationTime.user?.name || t('separation_time.modal.unknown_user', 'Usuario desconocido');
                  const totalProducts = separationTime.items.length;
                  const totalQty = separationTime.items.reduce((sum, item) => sum + item.qty, 0);

                  return (
                    <div
                      key={separationTime.id}
                      className="bg-background border border-border rounded-lg p-4 hover:border-primary transition-colors"
                    >
                      {/* Header: Tiempo y Fecha */}
                      <div className="flex items-center justify-between mb-4 pb-3 border-b border-border">
                        <div className="flex items-center gap-3">
                          <div className="bg-primary/10 rounded-lg p-2">
                            <Clock className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <div className="text-lg font-bold text-foreground">
                              {formatTimeMinutes(separationTime.time_minutes)}
                            </div>
                            <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                              <Clock className="w-3 h-3" />
                              {formatDate(separationTime.created_at)}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-start justify-end gap-2">
                          <div className="text-right">
                            <div className="text-sm font-semibold text-foreground">
                              {totalQty} {t('separation_time.modal.units', 'unidades')}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {totalProducts}{' '}
                              {totalProducts === 1
                                ? t('separation_time.modal.product', 'producto')
                                : t('separation_time.modal.products_plural', 'productos')}
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => handleStartEdit(separationTime)}
                            disabled={isProcessing}
                            title={t('separation_time.edit_action', 'Editar')}
                            data-testid={`separation-time-edit-record-button-${separationTime.id}`}
                          >
                            <FontAwesomeIcon icon={faPen} className="w-3.5 h-3.5" />
                            <span className="sr-only">{t('separation_time.edit_action', 'Editar')}</span>
                          </Button>
                        </div>
                      </div>

                      {/* Usuario */}
                      {separationTime.user && (
                        <div className="flex items-center gap-2 mb-3">
                          <User className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm text-foreground">{userName}</span>
                        </div>
                      )}

                      {/* Lista de productos */}
                      <div className="space-y-2">
                        {separationTime.items.map((item) => {
                          const productName = item.product?.name || `Producto ${item.product_id}`;
                          const productCode = item.product?.code || '';
                          const productImage = item.product?.img || null;

                          return (
                            <div key={item.id} className="flex items-center justify-between p-2 bg-muted/30 rounded-md">
                              <div className="flex-1 min-w-0">
                                <Product
                                  id={item.product_id}
                                  name={productName}
                                  src={buildImageUrl(productImage)}
                                  code={productCode}
                                  showImageModal={true}
                                  enableLineClamp={true}
                                />
                              </div>
                              <Badge variant="secondary" className="text-xs ml-3 flex-shrink-0">
                                {item.qty} {t('separation_time.modal.units', 'ud.')}
                              </Badge>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          )}
        </TabsContent>
      </Tabs>
    </ModalCenter>
  );
});

SeparationTimeModal.displayName = 'SeparationTimeModal';
